import queryIsHandleAvailable from '~/graphql/queries/queryIsHandleAvailable';
import createProfile from '~/graphql/mutations/createProfile';
import deleteAccount from '~/graphql/mutations/deleteAccount';
import { getItem, removeItem, setItem } from '~/utils/localStorageHelpers';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import queryProfileInvite from '../graphql/queries/queryProfileInvite';
import redeemProfileInvite from '../graphql/mutations/redeemProfileInvite';
import { HeadersWithoutApiKeyWorkaround } from './constants';
import setManagedProfileMutation from '../graphql/mutations/setManagedProfileMutation';

// this is the interface for afterLoginAction
// it can not be used in javascript
// interface afterLoginAction {
//     routePath: string;
//     routeParams: string;
// }

export const parseJwt = function parseJwt(token) {
    try {
        return JSON.parse(atob(token.split('.')[1]));
    } catch (e) {
        return null;
    }
};

async function requestCognito(requestType, params, customCognitoID) {
    const cognitoUrl = this.app.$config.cognitoUrl;
    let cognitoId = this.app.$config.cognitoId;
    if (customCognitoID) {
        cognitoId = customCognitoID;
    }

    if (!cognitoUrl || !cognitoId) {
        return Promise.reject('COGNITO_API_URL or COGNITO_CLIENT_ID unset');
    }

    const headers = {
        'Content-Type': 'application/x-amz-json-1.1',
        'X-Amz-Target': requestType,
        'X-Beams-Client': `web/${this.app.$config.webAppDomain.includes('alpha') ? 'alpha' : 'production'}/${this.app.$config.webBuildVersion}`,
    };
    try {
        const result = await axios({
            method: 'POST',
            url: cognitoUrl,
            headers: headers,
            data: {
                ClientId: cognitoId,
                ...params,
            },
        });
        if (result.status !== 200) {
            return Error(`signup failed ${result.status}`);
        } else {
            return result;
        }
    } catch (error) {
        throw error;
    }
}

const afterLoginStorageKey = 'beams:afterLoginAction';
const loginTypeStorageKey = 'beams:loginTypeStorageKey';
const profileInviteStorageKey = 'beams:profileInvite';

export const state = () => ({
    afterLoginRoute: JSON.parse(getItem(afterLoginStorageKey)) || undefined,
    username: null,
    resetPasswordSession: null,
    phoneNumberOrEmail: null,
    type: getItem(loginTypeStorageKey) || undefined,
    password: null,
    showConfirmationCode: false,
    confirmationCode: null,
    isCreatingProfileLoading: false,
    isCreatingProfileError: false,
    loginType: getItem('beams:loginType') || undefined,
    wantsPhoneSignup: false,
    resetPassword: false,
    profileInvite: JSON.parse(getItem(profileInviteStorageKey)) || undefined,
    signupBrandLink: null,
    isInOnboardingFlow: false,
});

export const actions = {
    async setOnboardingFlow(context, state) {
        await context.commit('setOnboardingFlow', state);
    },
    async changeProfile(context, { profile }) {
        await this.app.apolloProvider.defaultClient.mutate({
            mutation: setManagedProfileMutation.setManagedProfileMutation,
            variables: { profileID: profile.id },
            context: HeadersWithoutApiKeyWorkaround,
        });

        await context.commit('profile/resetMyProfile', {}, { root: true });
        await context.dispatch('loggedIn', { shouldRefreshToken: true, isNewSignup: false });
    },

    async fetchProfileInvite(context, { url }) {
        const result = await this.app.apolloProvider.defaultClient.query({
            query: queryProfileInvite.profileInvite,
            variables: {
                url,
            },
        });

        if (!result?.data?.profileInvite) {
            throw new Error('unable to fetch the profile invite');
        }

        await context.commit('setProfileInvite', result.data.profileInvite);
    },

    async saveAfterLoginRoute(context, setAfterLoginRoute) {
        await context.commit('setAfterLoginRoute', setAfterLoginRoute);
    },

    async saveSignupBrandLink(context, brandLink) {
        await context.commit('setSignupBrandLink', brandLink);
    },

    async signup(context, { email, password }) {
        const username = uuidv4();
        let userAttributes = [
            {
                Name: 'email',
                Value: email.replace(/\s/gi, ''),
            },
        ];
        if (!!context.getters['getSignupBrandLink']) {
            userAttributes.push({
                Name: 'custom:initial_profile_url',
                Value: context.getters['getSignupBrandLink']
                    .replace(/https:\/\//gi, '') //     remove https://
                    .replace(/\W/gi, '.') //            replace non-letter character with '.'
                    .replace(/\.+/g, '.'), //           collapse sequences of '.' into a single one
            });
        }
        await requestCognito.bind(this)('AWSCognitoIdentityProviderService.SignUp', {
            AuthFlow: 'USER_PASSWORD_AUTH',
            Password: password,
            Username: username,
            UserAttributes: userAttributes,
        });
        this.$analytics.trackEvent('sign_up_code_prompt', {
            sign_up_method: 'email',
            platform: 'web',
        });
        context.commit('setSignupSecondStep', { username, email, password });
    },

    async confirmSignup(context, { confirmationCode, type }) {
        await requestCognito.bind(this)('AWSCognitoIdentityProviderService.ConfirmSignUp', {
            Username: context.state.username,
            ConfirmationCode: confirmationCode,
        });
        await context.dispatch('login', { phoneNumberOrEmail: context.state.phoneNumberOrEmail, type: type, password: context.state.password });
    },

    async resendConfirmationCode(context) {
        return await requestCognito.bind(this)('AWSCognitoIdentityProviderService.ResendConfirmationCode', {
            Username: context.getters['getUsername'],
        });
    },

    async resetTempPassword(context, { password }) {
        try {
            const result = await requestCognito.bind(this)('AWSCognitoIdentityProviderService.RespondToAuthChallenge', {
                ChallengeName: 'NEW_PASSWORD_REQUIRED',
                ChallengeResponses: {
                    USERNAME: context.state.phoneNumberOrEmail.replace(/\s/gi, ''),
                    NEW_PASSWORD: password,
                },
                Session: context.state.resetPasswordSession,
            });
            await this.app.$auth.strategy.token.set(result.data.AuthenticationResult.IdToken);
            await this.app.$auth.strategy.refreshToken.set(result.data.AuthenticationResult.RefreshToken);
            await context.dispatch('loggedIn', { phoneOrEmailLogin: true });
        } catch (error) {
            console.error(error);
            throw error;
        }
    },

    async login(context, { phoneNumber, email, phoneNumberOrEmail, type, password }) {
        try {
            context.commit('setLoginType', type === 'phone' ? 'sms' : 'email');
            const username = phoneNumber || email || phoneNumberOrEmail;
            let result = await requestCognito.bind(this)('AWSCognitoIdentityProviderService.InitiateAuth', {
                AuthFlow: 'USER_PASSWORD_AUTH',
                AuthParameters: {
                    USERNAME: username.replace(/\s/gi, ''),
                    PASSWORD: password,
                },
            });
            if (result.data.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
                context.commit('resetPassword', { phoneNumberOrEmail: username, resetPasswordSession: result.data.Session });
                return;
            }

            await this.app.$auth.strategy.token.set(result.data.AuthenticationResult.IdToken);
            await this.app.$auth.strategy.refreshToken.set(result.data.AuthenticationResult.RefreshToken);

            await context.dispatch('loggedIn', { phoneOrEmailLogin: true });
        } catch (error) {
            console.error(error);
            throw error;
        }
    },

    async startForgotPassword(context, { type }) {
        await this.$router.push(`/forgotPassword?type=${type}`);
    },

    async forgotPassword(context, { email, phoneNumber }) {
        try {
            let phoneNumberOrEmail = email || phoneNumber;
            context.commit('setForgotPasswordStep', { phoneNumberOrEmail });
            let result = await requestCognito.bind(this)('AWSCognitoIdentityProviderService.ForgotPassword', {
                Username: phoneNumberOrEmail.replace(/\s/gi, ''),
            });
        } catch (error) {
            console.error(error);
            throw error;
        }
    },

    async confirmForgotPassword(context, { password, confirmationCode }) {
        try {
            let result = await requestCognito.bind(this)('AWSCognitoIdentityProviderService.ConfirmForgotPassword', {
                Username: context.state.phoneNumberOrEmail.replace(/\s/gi, ''),
                ConfirmationCode: confirmationCode,
                Password: password,
            });
        } catch (error) {
            console.error(error);
            throw error;
        }
    },

    async injectLogin(context, { token }) {
        await this.$auth.strategy.token.set('Bearer ' + token);

        await context.commit('setAfterLoginRoute', {
            routePath: this.app.router.currentRoute.path,
            routeQuery: this.app.router.currentRoute.query,
        });

        await context.dispatch('loggedIn');
    },

    async loggedIn(context, { shouldRefreshToken, phoneOrEmailLogin, isNewSignup } = { shouldRefreshToken: false, phoneOrEmailLogin: false, isNewSignup: false }) {
        if (shouldRefreshToken) {
            await this.$auth.refreshTokens();
        }
        const token = this.$auth.strategy.token.get();
        const parsedToken = parseJwt(token);
        const loginType = context.getters['getLoginType'];

        // existing user
        if (parsedToken['custom:profile_id']) {
            await context.dispatch('profile/fetchMyProfile', {}, { root: true });
            const user = await context.rootGetters['profile/getMyProfile'];
            if (isNewSignup) {
                this.$analytics.trackEvent('sign_up', {
                    sign_up_method: loginType,
                    platform: 'web',
                });
                if (!phoneOrEmailLogin) {
                    this.$analytics.trackEvent('social_auth_signed_up', { social_auth_method: loginType });
                }
            } else {
                if (!phoneOrEmailLogin) {
                    this.$analytics.trackEvent('social_auth_signed_in', { social_auth_method: loginType });
                }
                this.$analytics.trackEvent('login', {
                    sign_up_method: loginType,
                    platform: 'web',
                });
            }
            this.$analytics.reportUserLoggedIn(user, parsedToken, context.getters['isNewSignup']);

            if (!!context.getters['getSignupBrandLink']) {
                await context.dispatch('profile/updateProfile', { brandLink: context.getters['getSignupBrandLink'] });
            }

            if (context.getters['getAfterLoginRoute']) {
                const action = context.getters['getAfterLoginRoute'];
                await this.app.router.push({ path: action.routePath, query: action.routeQuery });
            } else if (isNewSignup) {
                await this.app.router.push('/onboarding');
            } else {
                await this.app.router.push('/dashboard');
            }

            context.commit('resetAuthProcess');
        }
        // new user
        else {
            await this.app.router.push('/profile/create');
        }
    },

    async initiateSocialLogin(context, { loginType }) {
        this.$analytics.trackEvent('social_auth_initiated', { social_auth_method: loginType });
        context.commit('setLoginType', loginType);
        setItem('beams:loginType', loginType);
        switch (loginType) {
            case 'google':
                await this.$auth.loginWith('awsCognito', { params: { identity_provider: 'Google' } });
                break;
            case 'facebook':
                await this.$auth.loginWith('awsCognito', { params: { identity_provider: 'Facebook' } });
                break;
            case 'siwa':
                await this.$auth.loginWith('awsCognito', { params: { identity_provider: 'SignInWithApple' } });
                break;
        }
    },

    async isHandleAvailable(_, { handle }) {
        const result = await this.app.apolloProvider.defaultClient.query({
            query: queryIsHandleAvailable.isHandleAvailable,
            variables: {
                handle,
            },
        });

        if (result?.data?.isHandleAvailable) {
            return result.data.isHandleAvailable.isAvailable;
        }
        return true;
    },

    async createProfile(context, { name, plan }) {
        if (context.state.isCreatingProfileLoading || context.state.isCreatingProfileError) {
            return;
        }

        context.commit('setCreatingProfileLoading', true);
        const result = await this.app.apolloProvider.defaultClient.mutate({
            mutation: createProfile.createProfileMutation,
            variables: {
                name,
                plan,
            },
            context: HeadersWithoutApiKeyWorkaround,
        });

        context.commit('setCreatingProfileLoading', false);
        if (result?.data?.createProfile) {
            context.commit('setCreatingProfileError');
            await context.dispatch('loggedIn', { shouldRefreshToken: true, isNewSignup: true });
            await context.commit('ui/hideModal', {}, { root: true });
            this.$analytics.trackEvent('profileCreated', { handle: result?.data?.createProfile.url });
        } else {
            const error = new Error('could not create profile');
            context.commit('setCreatingProfileError', error);
            throw error;
        }
    },

    async redeemProfileInvite(context, { url }) {
        await this.app.apolloProvider.defaultClient.mutate({
            mutation: redeemProfileInvite.redeemProfileInvite,
            variables: {
                url,
            },
            context: HeadersWithoutApiKeyWorkaround,
        });
        await context.dispatch('loggedIn', { shouldRefreshToken: true, isNewSignup: true });
    },

    async deleteAccount(context) {
        const result = await this.app.apolloProvider.defaultClient.mutate({
            mutation: deleteAccount.deleteAccountMutation,
            context: HeadersWithoutApiKeyWorkaround,
        });

        if (result?.data.deleteAccount.success) {
            await this.dispatch('logout');
        } else {
            throw new Error('could not delete account');
        }
    },

    async logoutWithoutRedirect(context) {
        try {
            await this.$auth.logout();
            await context.commit('profile/resetMyProfile', {}, { root: true });
        } catch (error) {
            console.error(error);
            throw error;
        }
    },

    async logout(context) {
        try {
            await context.dispatch('logoutWithoutRedirect');
            this.$analytics.reportUserLoggedOut();
            this.$analytics.trackEvent('logout');
            await this.app.router.push('/');
        } catch (error) {
            console.error(error);
            throw error;
        }
    },

    resetAuthProcess(context) {
        context.commit('resetAuthProcess');
    },
};

export const mutations = {
    resetAuthProcess(state) {
        state.wantsPhoneSignup = false;
        state.showConfirmationCode = false;
        state.resetPassword = false;
        state.resetPasswordSession = '';
        state.password = '';
        state.username = '';
        state.phoneNumberOrEmail = '';
        state.afterLoginAction = '';
        state.profileInvite = '';
        state.isInOnboardingFlow = false;
        removeItem(profileInviteStorageKey);
        removeItem(afterLoginStorageKey);
    },
    setOnboardingFlow(state, status) {
        state.isInOnboardingFlow = status;
    },
    setWantsPhoneSignup(state, wantsPhoneSignup) {
        state.wantsPhoneSignup = wantsPhoneSignup;
    },
    resetPassword(state, { phoneNumberOrEmail, resetPasswordSession }) {
        state.resetPassword = true;
        state.resetPasswordSession = resetPasswordSession;
        state.phoneNumberOrEmail = phoneNumberOrEmail;
    },
    setSignupSecondStep(state, { username, email, password }) {
        state.showConfirmationCode = true;
        state.username = username;
        state.phoneNumberOrEmail = email;
        state.password = password;
    },
    setSignupBrandLink(state, brandLink) {
        state.signupBrandLink = brandLink;
    },
    setForgotPasswordStep(state, { phoneNumberOrEmail }) {
        state.phoneNumberOrEmail = phoneNumberOrEmail;
    },
    setLoginType(state, loginType) {
        state.loginType = loginType;
    },
    setCreatingProfileError(state, error) {
        state.isCreatingProfileError = error;
    },
    setCreatingProfileLoading(state, loading) {
        state.isCreatingProfileLoading = loading;
    },
    setAfterLoginRoute(state, afterLoginRoute) {
        if (afterLoginRoute) {
            setItem(afterLoginStorageKey, JSON.stringify(afterLoginRoute));
        } else {
            removeItem(afterLoginStorageKey);
        }
        state.afterLoginRoute = afterLoginRoute;
    },
    setProfileInvite(state, profileInvite) {
        if (profileInvite) {
            setItem(profileInviteStorageKey, JSON.stringify(profileInvite));
        } else {
            removeItem(profileInviteStorageKey);
        }
        state.profileInvite = profileInvite;
    },
};

export const getters = {
    getIsInOnboardingFlow(state) {
        return state.isInOnboardingFlow;
    },
    getAfterLoginRoute(state) {
        return state.afterLoginRoute;
    },
    getUsername(state) {
        return state.username;
    },
    getSignupBrandLink(state) {
        return state.signupBrandLink;
    },
    getShowConfirmationCode(state) {
        return state.showConfirmationCode;
    },
    getLoginType(state) {
        return state.loginType;
    },
    getResetPassword(state) {
        return state.resetPassword;
    },
    getProfileInvite(state) {
        return state.profileInvite;
    },
};
